import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Check, Download, FileText } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { ViewQuotePdfDocument } from '../components/ViewQuotePdfDocument';
import { exportPdfPages, exportPdfPagesAsDataUri, renumberQuotePageFooters } from '../lib/pdfPageExport';
import '../styles/quotePdf.css';

const TERMS_PDF_PATH = '/documents/VMK_Algemene_Voorwaarden_Particulieren_versie_V2.pdf';

const formatDisplayDate = (locale) => {
  const d = new Date();
  return d.toLocaleDateString(locale === 'nl' ? 'nl-NL' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const ViewQuotePage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const { t, language } = useTranslation();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('terms');
  const [termsScrolledToEnd, setTermsScrolledToEnd] = useState(false);
  const [termsAcceptedAt, setTermsAcceptedAt] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [signature, setSignature] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [signedQuoteNumber, setSignedQuoteNumber] = useState('');
  const [pdfZoom, setPdfZoom] = useState(1.5);

  const termsScrollRef = useRef(null);
  const termsEndRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const pdfExportRef = useRef(null);

  const locale = language === 'nl' ? 'nl-NL' : 'en-US';
  const formatCurrency = (value) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);

  const signedDate = formatDisplayDate(language);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Missing token');
      return;
    }
    axios
      .get(`${API_URL}/api/view-quote/${token}`)
      .then((res) => {
        setQuote(res.data);
        setName(res.data.signed_by_name || res.data.recipient_name || '');
        setEmail(res.data.recipient_email || '');
        if (res.data.signed) {
          setStep('success');
          setSignedQuoteNumber(res.data.quote_number || '');
        }
        setError(null);
      })
      .catch((err) => {
        setError(err.response?.status === 404 ? 'not_found' : err.message);
        setQuote(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (searchParams.get('signed') === '1' && quote?.signed) {
      setStep('success');
    }
  }, [searchParams, quote?.signed]);

  const checkTermsScroll = useCallback(() => {
    const container = termsScrollRef.current;
    const sentinel = termsEndRef.current;
    if (!container || !sentinel) return;
    const containerBottom = container.scrollTop + container.clientHeight;
    const sentinelTop = sentinel.offsetTop - container.offsetTop;
    if (containerBottom >= sentinelTop - 8) {
      setTermsScrolledToEnd(true);
    }
  }, []);

  useEffect(() => {
    if (step !== 'terms') return undefined;
    const container = termsScrollRef.current;
    if (!container) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTermsScrolledToEnd(true);
        }
      },
      { root: container, threshold: 0.5 }
    );

    if (termsEndRef.current) {
      observer.observe(termsEndRef.current);
    }

    container.addEventListener('scroll', checkTermsScroll, { passive: true });
    checkTermsScroll();

    return () => {
      observer.disconnect();
      container.removeEventListener('scroll', checkTermsScroll);
    };
  }, [step, checkTermsScroll]);

  const getCoords = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const coords = getCoords(e);
    if (!coords) return;
    setIsDrawing(true);
    const ctx = signatureCanvasRef.current.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const coords = getCoords(e);
    if (!coords) return;
    const ctx = signatureCanvasRef.current.getContext('2d');
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const endDraw = (e) => {
    e.preventDefault();
    if (isDrawing && signatureCanvasRef.current) {
      setSignature(signatureCanvasRef.current.toDataURL('image/png'));
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignature('');
    }
  };

  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [step]);

  const handleAcceptTerms = () => {
    setTermsAcceptedAt(new Date().toISOString());
    setStep('sign');
  };

  const runSignedQuotePdfExport = async ({ asDataUri = false, filename } = {}) => {
    const container = pdfExportRef.current;
    if (!container) return null;

    container.classList.add('pdf-exporting');
    const restoreFooters = renumberQuotePageFooters(container, '[data-pdf-page]');

    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
    const images = container.querySelectorAll('img');
    await Promise.all(
      [...images].map(
        (img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              })
      )
    );

    await new Promise((resolve) => setTimeout(resolve, 300));
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const exportOptions = {
      scale: 2,
      marginMm: 0,
      pageSelector: '[data-pdf-page]',
    };

    try {
      if (asDataUri) {
        return await exportPdfPagesAsDataUri(container, exportOptions);
      }
      await exportPdfPages(container, filename, exportOptions);
      return true;
    } finally {
      container.classList.remove('pdf-exporting');
      restoreFooters();
    }
  };

  const generateSignedPdfDataUri = async () => {
    try {
      return await runSignedQuotePdfExport({ asDataUri: true });
    } catch (err) {
      console.error('Failed to generate signed quote PDF:', err);
      return null;
    }
  };

  const handleDownloadSignedPdf = async () => {
    setDownloading(true);
    try {
      await runSignedQuotePdfExport({
        filename: `Offerte-${quote?.quote_number || 'signed'}.pdf`,
      });
    } catch (err) {
      alert(t('viewQuote.downloadError'));
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const handleSignSubmit = async () => {
    setSubmitting(true);
    try {
      const quotePdfBase64 = await generateSignedPdfDataUri();
      const res = await axios.post(`${API_URL}/api/view-quote/${token}/sign`, {
        recipient_name: name.trim(),
        recipient_email: email.trim(),
        signature,
        terms_accepted_at: termsAcceptedAt,
        quote_pdf_base64: quotePdfBase64,
      });
      setSignedQuoteNumber(quote?.quote_number || '');
      setStep('success');
      if (!res.data?.email_sent) {
        console.warn('Signed quote saved but confirmation email may have failed');
      }
    } catch (err) {
      alert(t('viewQuote.acceptError') + ': ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const signedAgreement = {
    name: quote?.signed_by_name || name.trim() || quote?.recipient_name || '',
    date: quote?.signed_at
      ? new Date(quote.signed_at).toLocaleDateString(locale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : signedDate,
    signature: quote?.signature || signature || '',
  };

  const clientDetails = {
    name: name.trim() || quote?.recipient_name || '',
    ...(quote?.client_details || {}),
  };

  const total = quote?.total ?? 0;
  const termsPdfUrl = quote?.terms_pdf_url || TERMS_PDF_PATH;

  const pdfExportDocument = (
    <ViewQuotePdfDocument
      quoteNumber={quote?.quote_number || signedQuoteNumber}
      clientDetails={clientDetails}
      drawings={quote?.drawings || []}
      exportOptions={quote?.export_options || {}}
      subtotal={quote?.subtotal}
      signedAgreement={signedAgreement}
      t={t}
    />
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-lg text-gray-600">{t('viewQuote.loading')}</p>
      </div>
    );
  }

  if (error === 'not_found' || (!quote && token)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-lg text-gray-700">{t('viewQuote.quoteNotFound')}</p>
      </div>
    );
  }

  const pdfViewerWidth = Math.round(920 * pdfZoom);
  const pdfViewerHeight = Math.round(4800 * pdfZoom);

  const pdfExportHost = (
    <div
      ref={pdfExportRef}
      id="quote-content"
      className="view-quote-pdf-export-host quote-content-root bg-white"
    >
      {pdfExportDocument}
    </div>
  );

  if (step === 'success') {
    return (
      <>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-12 max-w-lg w-full text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600 stroke-[2.5]" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              {t('viewQuote.acceptSuccessTitle')}
            </h2>
            <p className="text-base text-gray-600 mb-2">{t('viewQuote.acceptSuccess')}</p>
            <p className="text-base text-gray-500 mb-8">{t('viewQuote.emailSentConfirmation')}</p>

            <div className="space-y-3">
              <Button
                onClick={handleDownloadSignedPdf}
                disabled={downloading}
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading ? t('viewQuote.downloading') : t('viewQuote.downloadSignedQuote')}
              </Button>
            </div>
          </div>
        </div>
        {pdfExportHost}
      </>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8 text-base">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-base font-semibold text-gray-800">VMK Kozijnen B.V.</p>
            <p className="text-sm text-gray-500">
              {t('viewQuote.quoteReference')}: {quote?.quote_number}
            </p>
          </div>
          <div className="text-right">
            <p className="text-base text-gray-600">{quote?.recipient_name}</p>
            <p className="text-2xl font-semibold text-gray-900">
              {formatCurrency(total)}
            </p>
          </div>
        </div>

        <div className={step === 'terms' ? 'space-y-6' : 'grid grid-cols-1 lg:grid-cols-3 gap-8'}>
          <div className={step === 'terms' ? '' : 'lg:col-span-2'}>
            {step === 'terms' ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-100 bg-slate-50 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <FileText className="h-6 w-6 text-orange-600 shrink-0" />
                    <div>
                      <h1 className="text-2xl font-semibold text-gray-900">
                        {t('viewQuote.termsTitle')}
                      </h1>
                      <p className="text-base text-gray-600">{t('viewQuote.termsSubtitle')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm text-gray-500 mr-1">{t('viewQuote.pdfZoom')}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 w-9 text-lg"
                      disabled={pdfZoom <= 1}
                      onClick={() => setPdfZoom((z) => Math.max(1, +(z - 0.15).toFixed(2)))}
                      aria-label={t('viewQuote.zoomOut')}
                    >
                      −
                    </Button>
                    <span className="text-base font-medium text-gray-700 w-14 text-center">
                      {Math.round(pdfZoom * 100)}%
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 w-9 text-lg"
                      disabled={pdfZoom >= 2}
                      onClick={() => setPdfZoom((z) => Math.min(2, +(z + 0.15).toFixed(2)))}
                      aria-label={t('viewQuote.zoomIn')}
                    >
                      +
                    </Button>
                  </div>
                </div>

                <div
                  ref={termsScrollRef}
                  className="h-[78vh] overflow-auto bg-slate-200 p-4 sm:p-6"
                  onScroll={checkTermsScroll}
                >
                  <div
                    className="mx-auto bg-white shadow-md"
                    style={{ width: pdfViewerWidth, maxWidth: 'none' }}
                  >
                    <iframe
                      src={`${termsPdfUrl}#toolbar=0&navpanes=0`}
                      title={t('viewQuote.termsTitle')}
                      className="w-full border-0 block"
                      style={{ height: pdfViewerHeight }}
                    />
                  </div>
                  <div ref={termsEndRef} className="h-8 w-full" aria-hidden />
                </div>

                <div className="px-6 py-5 border-t border-gray-100 bg-white">
                  {!termsScrolledToEnd ? (
                    <p className="text-base text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                      {t('viewQuote.scrollToAccept')}
                    </p>
                  ) : (
                    <p className="text-base text-green-800 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4">
                      {t('viewQuote.termsReadComplete')}
                    </p>
                  )}
                  <Button
                    className="w-full h-12 text-base bg-orange-600 hover:bg-orange-700"
                    disabled={!termsScrolledToEnd}
                    onClick={handleAcceptTerms}
                  >
                    {t('viewQuote.acceptTerms')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-10">
                <h1 className="text-2xl font-semibold text-gray-900 mb-3">
                  {t('quote.template.agreementTitle')}
                </h1>
                <p className="text-base text-gray-600 mb-8">{t('quote.template.agreementIntro')}</p>

                <div className="space-y-6 max-w-2xl">
                  <div>
                    <Label className="text-base text-gray-700">{t('quote.template.agreementName')}</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-11 text-base" />
                  </div>
                  <div>
                    <Label className="text-base text-gray-700">{t('viewQuote.emailAddress')}</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-2 h-11 text-base"
                    />
                  </div>
                  <div>
                    <Label className="text-base text-gray-700">{t('quote.template.agreementDate')}</Label>
                    <Input value={signedDate} readOnly className="mt-2 h-11 text-base bg-gray-50" />
                  </div>
                  <div>
                    <Label className="text-base text-gray-700">{t('quote.template.agreementSignature')}</Label>
                    <canvas
                      ref={signatureCanvasRef}
                      width={560}
                      height={160}
                      className="mt-2 w-full border-2 border-gray-300 rounded bg-white cursor-crosshair touch-none"
                      style={{ maxWidth: '100%', height: '160px' }}
                      onMouseDown={startDraw}
                      onMouseMove={draw}
                      onMouseUp={endDraw}
                      onMouseLeave={endDraw}
                      onTouchStart={startDraw}
                      onTouchMove={draw}
                      onTouchEnd={endDraw}
                    />
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="mt-2 text-base text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {t('viewQuote.clearField')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={step === 'terms' ? '' : 'lg:col-span-1'}>
            <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${step === 'sign' ? 'sticky top-6' : ''}`}>
              <h2 className="text-xl font-semibold text-gray-900">
                {step === 'terms' ? t('viewQuote.termsSidebarTitle') : t('viewQuote.accept')}
              </h2>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {formatCurrency(total)}
              </p>
              <p className="mt-1 text-sm text-gray-500">{t('viewQuote.inclVat')}</p>

              {step === 'sign' ? (
                <>
                  <p className="mt-4 text-base text-gray-600">{t('viewQuote.signatureInstruction')}</p>
                  <Button
                    className="mt-6 w-full h-12 text-base bg-green-600 hover:bg-green-700"
                    disabled={!name.trim() || !email.trim() || !signature || submitting}
                    onClick={handleSignSubmit}
                  >
                    {submitting ? t('viewQuote.submitting') : t('viewQuote.signAndAccept')}
                  </Button>
                </>
              ) : (
                <p className="mt-4 text-base text-gray-600">{t('viewQuote.termsSidebarHint')}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    {pdfExportHost}
    </>
  );
};

export default ViewQuotePage;
